import Phaser from "phaser";
import { installLetterboxFit } from "../ui/responsive";
import { cycleUiScale, equippedSkinColor, getOrCreatePlayerId, loadPlayerMemory, loadSettings, setLanguage, toggleBetaFeature, toggleHighContrastText, toggleMuted, type BetaFeatureKey } from "../../ai/localMemory";
import { t, toggleLanguage } from "../../i18n";
import { skinHex } from "../skins";
import { DONATION_URL } from "../../config";
import { rivalryStageLabel } from "../../ui/rivalry";
import { AudioManager } from "../AudioManager";
import { BATTLE_MODES, type BattleMode } from "../battleMode";
import { dailyChallengeForDate } from "../dailyChallenge";
import mapConfig from "../data/mapConfig.json";

export class MenuScene extends Phaser.Scene {
  private settings = loadSettings();
  private selectedMode: BattleMode = "normal";

  constructor() {
    super("MenuScene");
  }

  create() {
    installLetterboxFit(this);
    this.settings = loadSettings();
    AudioManager.syncFromSettings();
    // Deep link: ?daily=1 (from a shared link) jumps straight into today's challenge.
    if (new URLSearchParams(window.location.search).get("daily") === "1") {
      window.history.replaceState({}, "", window.location.pathname);
      this.scene.start("BattleScene", { mode: "daily", dailySeed: dailyChallengeForDate(new Date()).seed });
      return;
    }
    this.add.image(640, 360, mapConfig.mapAssetKey).setDisplaySize(1280, 720);
    this.add.rectangle(640, 360, 1280, 720, 0x08070a, 0.46);
    this.drawCommanderBanner();
    this.add
      .text(640, 166, "Duel Siege AI", {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "62px",
        color: "#f7f2df",
        stroke: "#130e12",
        strokeThickness: 8
      })
      .setOrigin(0.5);
    this.add
      .text(640, 242, t(this.settings.language, "menuSubtitle"), {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: this.settings.language === "ko" ? "21px" : "22px",
        color: "#d9e9ff",
        stroke: "#130e12",
        strokeThickness: 5
      })
      .setOrigin(0.5);

    this.drawLanguageToggle();
    this.drawSupportButton();
    this.drawCareerButton();
    this.drawShopButton();
    this.drawRivalryBadge();
    this.drawNemesisRematch();
    this.drawWarRoomPanel();
    this.drawDailyChallenge();
  }

  /** When the rivalry has truly escalated, offer a dramatic Hard rematch with the named rival. */
  /** Optional "Support" link — only rendered when the owner has set DONATION_URL in config.ts. */
  private drawSupportButton() {
    if (!DONATION_URL) return;
    const button = this.add.container(1014, 54);
    const background = this.add.rectangle(0, 0, 100, 34, 0x2a1420, 0.85).setStrokeStyle(1, 0xff8fb0, 0.85);
    const label = this.add
      .text(0, 0, t(this.settings.language, "support"), {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "14px",
        color: "#ffd0de",
        stroke: "#08070a",
        strokeThickness: 3
      })
      .setOrigin(0.5);
    button.add([background, label]);
    button.setSize(100, 34).setInteractive({ useHandCursor: true });
    button.on("pointerover", () => background.setFillStyle(0x40202f, 1));
    button.on("pointerout", () => background.setFillStyle(0x2a1420, 0.85));
    button.on("pointerdown", () => window.open(DONATION_URL, "_blank", "noopener"));
  }

  private drawNemesisRematch() {
    const memory = loadPlayerMemory(getOrCreatePlayerId());
    if (memory.rivalryStage !== "obsessed" && memory.rivalryStage !== "respectful_enemy") return;
    const lang = this.settings.language;
    const label = memory.nickname
      ? (lang === "ko" ? `⚔️ ${memory.nickname}` : `⚔️ ${memory.nickname}`)
      : t(lang, "nemesisRematch");
    const button = this.add.container(404, 54);
    const background = this.add.rectangle(0, 0, 150, 34, 0x3a1414, 0.92).setStrokeStyle(1, 0xff6b5a, 0.95);
    const text = this.add
      .text(0, 0, label, {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "13px",
        color: "#ffd0c4",
        stroke: "#08070a",
        strokeThickness: 3,
        align: "center",
        wordWrap: { width: 138 }
      })
      .setOrigin(0.5);
    button.add([background, text]);
    button.setSize(150, 34).setInteractive({ useHandCursor: true });
    button.on("pointerover", () => background.setFillStyle(0x5a1d1d, 1));
    button.on("pointerout", () => background.setFillStyle(0x3a1414, 0.92));
    button.on("pointerdown", () => {
      AudioManager.playSfx("click");
      this.scene.start("BattleScene", { mode: "hard" });
    });
  }

  private drawDailyChallenge() {
    const memory = loadPlayerMemory(getOrCreatePlayerId());
    const challenge = dailyChallengeForDate(new Date());
    const lang = this.settings.language;
    const record = memory.dailyChallenge;
    const onToday = record?.dateKey === challenge.dateKey;
    const best = onToday ? record!.bestScore : 0;
    const streak = record?.streak ?? 0;
    const cleared = Boolean(onToday && record!.completed);
    const font = "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif";

    const container = this.add.container(640, 688);
    const background = this.add.rectangle(0, 0, 590, 56, 0x171019, 0.9).setStrokeStyle(2, 0xffcf6b, 0.85);
    container.add(background);

    const button = this.add.container(-228, 0);
    const buttonBg = this.add.rectangle(0, 0, 122, 48, 0x4a2f12, 0.98).setStrokeStyle(2, 0xffcf6b, 1);
    const buttonText = this.add
      .text(0, 0, t(lang, "dailyChallenge"), {
        fontFamily: font,
        fontSize: lang === "ko" ? "16px" : "15px",
        color: "#ffe9b8",
        stroke: "#08070a",
        strokeThickness: 4,
        align: "center",
        wordWrap: { width: 112 }
      })
      .setOrigin(0.5);
    button.add([buttonBg, buttonText]);
    button.setSize(122, 48).setInteractive({ useHandCursor: true });
    button.on("pointerover", () => buttonBg.setFillStyle(0x6a4418, 1));
    button.on("pointerout", () => buttonBg.setFillStyle(0x4a2f12, 0.98));
    button.on("pointerdown", () => {
      AudioManager.playSfx("click");
      this.scene.start("BattleScene", { mode: "daily", dailySeed: challenge.seed });
    });
    container.add(button);

    const mods = challenge.modifiers.map((modifier) => t(lang, `dmod_${modifier.id}` as Parameters<typeof t>[1])).join(" · ");
    const info = this.add
      .text(-150, -14, `${t(lang, "dailyToday")}: ${mods}`, {
        fontFamily: font,
        fontSize: "14px",
        color: "#ffd98a",
        stroke: "#08070a",
        strokeThickness: 3,
        wordWrap: { width: 348 }
      })
      .setOrigin(0, 0.5);
    container.add(info);

    const stat = `${t(lang, "dailyBestScore")} ${best}    ·    ${t(lang, "dailyStreak")} ${streak}${cleared ? `    ·    ${t(lang, "dailyCompleted")}` : ""}`;
    const statText = this.add
      .text(-150, 14, stat, {
        fontFamily: font,
        fontSize: "13px",
        color: cleared ? "#9ff0b0" : "#b9b1a0",
        stroke: "#08070a",
        strokeThickness: 3
      })
      .setOrigin(0, 0.5);
    container.add(statText);

    const ranks = this.add.container(248, 0);
    const ranksBg = this.add.rectangle(0, 0, 82, 42, 0x101820, 0.88).setStrokeStyle(1, 0xffcf6b, 0.7);
    const ranksText = this.add
      .text(0, 0, t(lang, "leaderboardShort"), {
        fontFamily: font,
        fontSize: "13px",
        color: "#ffe9b8",
        stroke: "#08070a",
        strokeThickness: 3,
        align: "center",
        wordWrap: { width: 74 }
      })
      .setOrigin(0.5);
    ranks.add([ranksBg, ranksText]);
    ranks.setSize(82, 42).setInteractive({ useHandCursor: true });
    ranks.on("pointerover", () => ranksBg.setFillStyle(0x17364a, 0.95));
    ranks.on("pointerout", () => ranksBg.setFillStyle(0x101820, 0.88));
    ranks.on("pointerdown", () => {
      AudioManager.playSfx("click");
      this.scene.start("LeaderboardScene");
    });
    container.add(ranks);
  }

  private drawCommanderBanner() {
    const color = equippedSkinColor(loadPlayerMemory(getOrCreatePlayerId()));
    const pennant = (x: number) => {
      // Crossbar/pole above the pennant.
      this.add.rectangle(x, 112, 56, 7, 0x6b5a3a, 1).setStrokeStyle(1, 0x1a1410, 0.85).setDepth(1);
      // Downward heraldic pennant in the equipped banner color.
      this.add.triangle(x, 150, -23, -32, 23, -32, 0, 36, color, 0.96).setStrokeStyle(2, 0x1a1410, 0.9).setDepth(1);
    };
    pennant(412);
    pennant(868);
  }

  private drawRivalryBadge() {
    const memory = loadPlayerMemory(getOrCreatePlayerId());
    if (memory.battleCount <= 0 && !memory.nickname) return;
    const label = this.settings.language === "ko"
      ? `관계 ${rivalryStageLabel(memory.rivalryStage, "ko")} · 점수 ${memory.rivalryScore}${memory.nickname ? ` · 별명 ${memory.nickname}` : ""}`
      : `Rivalry ${rivalryStageLabel(memory.rivalryStage, "en")} · Score ${memory.rivalryScore}${memory.nickname ? ` · ${memory.nickname}` : ""}`;
    this.add
      .text(640, 282, label, {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: this.settings.language === "ko" ? "16px" : "15px",
        color: skinHex(memory.equippedSkin),
        stroke: "#08070a",
        strokeThickness: 4,
        wordWrap: { width: 780 }
      })
      .setOrigin(0.5);
    const progressLabel = `${t(this.settings.language, "commanderLevel")} ${memory.commanderLevel} · ${t(this.settings.language, "warFunds")} ${memory.warFunds}`;
    this.add
      .text(640, 306, progressLabel, {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: this.settings.language === "ko" ? "14px" : "13px",
        color: "#a7d8ff",
        stroke: "#08070a",
        strokeThickness: 3
      })
      .setOrigin(0.5);
  }

  private drawCareerButton() {
    const button = this.add.container(148, 54);
    const background = this.add.rectangle(0, 0, 112, 34, 0x101820, 0.78).setStrokeStyle(1, 0xc9b878, 0.85);
    const label = this.add
      .text(0, 0, t(this.settings.language, "career"), {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "14px",
        color: "#f7e9bb",
        stroke: "#08070a",
        strokeThickness: 3
      })
      .setOrigin(0.5);
    button.add([background, label]);
    button.setSize(112, 34);
    button.setInteractive({ useHandCursor: true });
    button.on("pointerover", () => background.setFillStyle(0x17364a, 0.95));
    button.on("pointerout", () => background.setFillStyle(0x101820, 0.78));
    button.on("pointerdown", () => this.scene.start("CareerScene"));
  }

  private drawShopButton() {
    const button = this.add.container(266, 54);
    const background = this.add.rectangle(0, 0, 112, 34, 0x101820, 0.78).setStrokeStyle(1, 0xc9b878, 0.85);
    const label = this.add
      .text(0, 0, t(this.settings.language, "shop"), {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "14px",
        color: "#f7e9bb",
        stroke: "#08070a",
        strokeThickness: 3
      })
      .setOrigin(0.5);
    button.add([background, label]);
    button.setSize(112, 34);
    button.setInteractive({ useHandCursor: true });
    button.on("pointerover", () => background.setFillStyle(0x17364a, 0.95));
    button.on("pointerout", () => background.setFillStyle(0x101820, 0.78));
    button.on("pointerdown", () => this.scene.start("ShopScene"));
  }

  private drawLanguageToggle() {
    const button = this.add.container(1132, 54);
    const background = this.add.rectangle(0, 0, 112, 34, 0x101820, 0.78).setStrokeStyle(1, 0xc9b878, 0.85);
    const label = this.add
      .text(0, 0, t(this.settings.language, "language"), {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "14px",
        color: "#f7e9bb",
        stroke: "#08070a",
        strokeThickness: 3
      })
      .setOrigin(0.5);
    button.add([background, label]);
    button.setSize(112, 34);
    button.setInteractive({ useHandCursor: true });
    button.on("pointerover", () => background.setFillStyle(0x17364a, 0.95));
    button.on("pointerout", () => background.setFillStyle(0x101820, 0.78));
    button.on("pointerdown", () => {
      setLanguage(toggleLanguage(this.settings.language));
      this.scene.restart();
    });
  }

  private drawWarRoomPanel() {
    const panel = this.add.container(640, 472);
    const background = this.add.rectangle(0, 0, 590, 292, 0x11131b, 0.88).setStrokeStyle(2, 0xc9b878, 0.9);
    const inner = this.add.rectangle(0, 0, 562, 264, 0x10283a, 0.38).setStrokeStyle(1, 0x5db7d0, 0.42);
    panel.add([background, inner]);

    const start = this.add.container(0, -42);
    const startBackground = this.add.rectangle(0, 0, 314, 66, 0x17364a, 0.98).setStrokeStyle(2, 0x8de7ff, 1);
    const startText = this.add
      .text(0, 0, t(this.settings.language, "enterBattle"), {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: this.settings.language === "ko" ? "27px" : "28px",
        color: "#f7f2df",
        stroke: "#130e12",
        strokeThickness: 5
      })
      .setOrigin(0.5);
    start.add([startBackground, startText]);
    start.setSize(314, 66);
    start.setInteractive({ useHandCursor: true });
    start.on("pointerover", () => startBackground.setFillStyle(0x235372, 1));
    start.on("pointerout", () => startBackground.setFillStyle(0x17364a, 0.98));
    start.on("pointerdown", () => {
      AudioManager.playSfx("click");
      this.scene.start("BattleScene", { mode: this.selectedMode });
    });
    panel.add(start);
    this.drawModeSelector(panel);

    const tutorial = this.add.container(0, 16);
    const tutorialBackground = this.add.rectangle(0, 0, 210, 36, 0x101820, 0.88).setStrokeStyle(1, 0xc9b878, 0.82);
    const tutorialText = this.add
      .text(0, 0, t(this.settings.language, "tutorial"), {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: this.settings.language === "ko" ? "17px" : "16px",
        color: "#f7e9bb",
        stroke: "#08070a",
        strokeThickness: 3
      })
      .setOrigin(0.5);
    tutorial.add([tutorialBackground, tutorialText]);
    tutorial.setSize(210, 36);
    tutorial.setInteractive({ useHandCursor: true });
    tutorial.on("pointerover", () => tutorialBackground.setFillStyle(0x17364a, 0.95));
    tutorial.on("pointerout", () => tutorialBackground.setFillStyle(0x101820, 0.88));
    tutorial.on("pointerdown", () => this.scene.start("BattleScene", { forceTutorial: true }));
    panel.add(tutorial);

    this.drawBetaToggles(panel);
    this.drawAccessibilityToggles(panel);
  }

  private drawModeSelector(panel: Phaser.GameObjects.Container) {
    // Only the selectable modes appear here; "daily" has its own entry point.
    const labelKey: Record<"normal" | "hard" | "endless", "modeNormal" | "modeHard" | "modeEndless"> = {
      normal: "modeNormal",
      hard: "modeHard",
      endless: "modeEndless"
    };
    const buttons: Array<{ mode: BattleMode; bg: Phaser.GameObjects.Rectangle; text: Phaser.GameObjects.Text }> = [];
    const refresh = () => {
      buttons.forEach(({ mode, bg, text }) => {
        const on = mode === this.selectedMode;
        bg.setFillStyle(on ? 0x235372 : 0x101820, on ? 1 : 0.8);
        bg.setStrokeStyle(1, on ? 0x8de7ff : 0xc9b878, on ? 1 : 0.6);
        text.setColor(on ? "#ddf8ff" : "#b9b1a0");
      });
    };
    BATTLE_MODES.forEach((mode, index) => {
      const x = -118 + index * 118;
      const button = this.add.container(x, -104);
      const bg = this.add.rectangle(0, 0, 110, 30, 0x101820, 0.8).setStrokeStyle(1, 0xc9b878, 0.6);
      const text = this.add
        .text(0, 0, t(this.settings.language, labelKey[mode as keyof typeof labelKey]), {
          fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
          fontSize: "14px",
          color: "#b9b1a0",
          stroke: "#08070a",
          strokeThickness: 3
        })
        .setOrigin(0.5);
      button.add([bg, text]);
      button.setSize(110, 30);
      button.setInteractive({ useHandCursor: true });
      button.on("pointerdown", () => {
        this.selectedMode = mode;
        AudioManager.playSfx("click");
        refresh();
      });
      panel.add(button);
      buttons.push({ mode, bg, text });
    });
    refresh();
  }

  private drawBetaToggles(panel: Phaser.GameObjects.Container) {
    const labels: Array<{ key: BetaFeatureKey; label: string }> = [
      { key: "cameraZoom", label: t(this.settings.language, "zoomMap") },
      { key: "spriteAnimations", label: t(this.settings.language, "unitAnim") },
      { key: "contactCombat", label: t(this.settings.language, "clashCombat") }
    ];
    const title = this.add
      .text(0, 46, t(this.settings.language, "betaSwitches"), {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "16px",
        color: "#f7e9bb",
        stroke: "#08070a",
        strokeThickness: 3
      })
      .setOrigin(0.5);
    panel.add(title);

    labels.forEach((item, index) => {
      const x = -174 + index * 174;
      const button = this.add.container(x, 90);
      const frame = this.add.image(0, 0, this.settings.beta[item.key] ? "build_card_selected" : "build_card").setDisplaySize(150, 46);
      const text = this.add
        .text(0, 0, this.toggleText(item.label, this.settings.beta[item.key]), {
          fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
          fontSize: this.settings.language === "ko" ? "12px" : "13px",
          color: this.settings.beta[item.key] ? "#ddf8ff" : "#b9b1a0",
          stroke: "#08070a",
          strokeThickness: 3,
          fixedWidth: 132
        })
        .setOrigin(0.5)
        .setAlign("center");
      button.add([frame, text]);
      button.setSize(150, 46);
      button.setInteractive({ useHandCursor: true });
      button.on("pointerdown", () => {
        this.settings = toggleBetaFeature(item.key);
        frame.setTexture(this.settings.beta[item.key] ? "build_card_selected" : "build_card");
        text.setText(this.toggleText(item.label, this.settings.beta[item.key]));
        text.setColor(this.settings.beta[item.key] ? "#ddf8ff" : "#b9b1a0");
      });
      panel.add(button);
    });
  }

  private drawAccessibilityToggles(panel: Phaser.GameObjects.Container) {
    const title = this.add
      .text(0, 118, t(this.settings.language, "accessibility"), {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "16px",
        color: "#f7e9bb",
        stroke: "#08070a",
        strokeThickness: 3
      })
      .setOrigin(0.5);
    panel.add(title);
    panel.add(
      this.createSettingButton(-150, 156, `${t(this.settings.language, "highContrast")}: ${this.settings.highContrastText ? t(this.settings.language, "on") : t(this.settings.language, "off")}`, () => {
        this.settings = toggleHighContrastText();
        this.scene.restart();
      })
    );
    panel.add(
      this.createSettingButton(0, 156, `${t(this.settings.language, "uiScale")}: ${Math.round((this.settings.uiScale ?? 1) * 100)}%`, () => {
        this.settings = cycleUiScale();
        this.scene.restart();
      })
    );
    panel.add(
      this.createSettingButton(150, 156, `${t(this.settings.language, "audio")}: ${this.settings.muted ? t(this.settings.language, "audioOff") : t(this.settings.language, "audioOn")}`, () => {
        this.settings = toggleMuted();
        AudioManager.setMuted(this.settings.muted);
        if (!this.settings.muted) AudioManager.playSfx("click");
        this.scene.restart();
      })
    );
  }

  private createSettingButton(x: number, y: number, label: string, onClick: () => void) {
    const button = this.add.container(x, y);
    const frame = this.add.image(0, 0, "build_card").setDisplaySize(172, 42);
    const text = this.add
      .text(0, 0, label, {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: this.settings.language === "ko" ? "13px" : "12px",
        color: "#f7f2df",
        stroke: "#08070a",
        strokeThickness: 3,
        fixedWidth: 154
      })
      .setOrigin(0.5)
      .setAlign("center");
    button.add([frame, text]);
    button.setSize(172, 42);
    button.setInteractive({ useHandCursor: true });
    button.on("pointerdown", onClick);
    return button;
  }

  private toggleText(label: string, enabled: boolean) {
    return `${label}: ${enabled ? t(this.settings.language, "on") : t(this.settings.language, "off")}`;
  }
}

